import React from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArthaLogo } from "@/components/ArthaLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { CurrencySelector } from "@/components/CurrencySelector";
import { useTheme } from "@/lib/theme";
import {
  LayoutDashboard, Building2, ClipboardCheck, FolderLock,
  CreditCard, Users2, Bot, Network, GitFork, Settings as SettingsIcon,
  Rocket, LogOut
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/app/register", label: "New Entity", icon: Rocket, accent: true },
  { to: "/app/entities", label: "Entities", icon: Building2 },
  { to: "/app/org", label: "Org Chart", icon: GitFork },
  { to: "/app/compliance", label: "Compliance", icon: ClipboardCheck },
  { to: "/app/documents", label: "Documents", icon: FolderLock },
  { to: "/app/payments", label: "Payments", icon: CreditCard },
  { to: "/app/partners", label: "Partners", icon: Users2 },
  { to: "/app/assistant", label: "AI Assistant", icon: Bot },
  { to: "/app/ecosystem", label: "Ecosystem", icon: Network },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

const DashboardShell = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-surface-app text-ink-body flex">
      <aside className="w-64 shrink-0 border-r border-border-subtle flex flex-col" data-testid="dashboard-sidebar">
        <div className="px-6 py-6 border-b border-border-subtle flex items-center justify-between">
          <Link to="/app/dashboard" data-testid="sidebar-logo-link">
            <ArthaLogo size={26} variant={theme === "dark" ? "light" : "dark"} />
          </Link>
          <div className="flex items-center gap-2">
            <CurrencySelector />
            <ThemeToggle />
          </div>
        </div>
        <nav className="flex-1 overflow-auto py-4 px-3">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ink-muted font-mono-accent px-3 mb-3">
            Workspace
          </div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              end={n.to === "/app/dashboard"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm tracking-tight mb-0.5 transition-colors ${
                  isActive
                    ? "bg-surface-card text-artha-amber border-l-2 border-artha-amber pl-[10px]"
                    : n.accent
                      ? "text-artha-amber hover:bg-surface-card"
                      : "text-ink-muted hover:text-ink-body hover:bg-surface-card"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border-subtle">
          <div className="px-3 py-3">
            <div className="text-xs text-ink-muted font-mono-accent truncate" data-testid="sidebar-user-email">{user?.email}</div>
            <div className="text-sm text-ink-body mt-0.5 truncate">{user?.name}</div>
          </div>
          <button
            onClick={() => { logout(); nav("/"); }}
            data-testid="sidebar-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:text-artha-amber transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        {user?.is_demo && <DemoBanner />}
        <Outlet />
      </main>
    </div>
  );
};

const DemoBanner = () => (
  <div className="bg-artha-amber text-artha-ink px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap" data-testid="demo-banner">
    <div className="text-xs font-mono-accent uppercase tracking-[0.22em] flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-artha-ink animate-pulse" />
      Demo sandbox — data is pre-seeded and will be cleared periodically
    </div>
    <Link to="/signup" data-testid="demo-banner-signup"
      className="text-xs font-mono-accent uppercase tracking-[0.22em] underline underline-offset-4 hover:no-underline">
      Create a real account →
    </Link>
  </div>
);

export default DashboardShell;
